import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import {
  X,
  Calendar,
  Clock,
  User,
  Building,
  Target,
  Flag,
  MapPin,
  FileText,
  Users,
  ArrowRight,
  Save,
  RefreshCw,
  AlertCircle,
  Plus,
  Search,
  Check,
  Activity,
  Link
} from 'lucide-react';
import entityRelationService from '../../services/entityRelationService';
import toast from 'react-hot-toast';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import EntitySelectionModal from '../../components/EntityManagement/EntitySelectionModal';
import dataGovernanceService from '../../services/dataGovernanceService';
import { AlertTriangle } from 'lucide-react';
import {
  ValidationSummary,
  FieldError,
  getInputClassName,
  getSelectClassName,
  getTextareaClassName,
  isEmpty,
  isValidEmail,
  scrollToFirstError
} from '../../components/Forms/FormValidation';
import AutocompleteTextField from '../../components/Forms/AutocompleteTextField';
import { getSuggestionConfig } from '../../components/Forms/TextFieldWithSuggestions';

// Helper function to convert UTC date to local date string for HTML date input
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    // Create a Date object from the UTC string
    const date = new Date(dateString);
    // Get the local date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    // Return in YYYY-MM-DD format for HTML date input
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.warn('ActivityForm: Failed to format date', error);
    return '';
  }
};

const ActivityForm = ({ activity, onClose, onSuccess, initialData = null }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  const tenantCodeRef = useRef(getProtectedTenantCode());
  
  // Load entity configuration with proper caching and loading states
  const { config: entityConfig, loading: configLoading, error: configError } = useEntityConfig('activity');
  
  const [formData, setFormData] = useState({
    activityType: '',
    subject: '',
    description: '',
    priority: 'Medium',
    status: 'Planned',
    selectedEntityTypes: [], // Changed to array for multiple entity types
    relatedEntities: [], // Array of selected entities
    dueDate: '',
    startDate: '',
    endDate: '',
    followUpDate: '',
    estimatedDuration: '',
    actualDuration: '',
    location: '',
    notes: '',
    parentActivityId: '',
    salespersonId: '', // Salesperson who created/owns the activity
    outcome: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [searchResults, setSearchResults] = useState({
    customers: [],
    leads: [],
    opportunities: [],
    activities: []
  });
  
  // Dynamic list values
  const [activityTypeOptions, setActivityTypeOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [showEntitySearch, setShowEntitySearch] = useState({
    Customer: false,
    Lead: false,
    Opportunity: false,
    SaleNote: false
  });
  const [showParentSearch, setShowParentSearch] = useState(false);
  const [entitySearchTerms, setEntitySearchTerms] = useState({
    Customer: '',
    Lead: '',
    Opportunity: '',
    SaleNote: ''
  });
  const [parentSearchTerm, setParentSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState({
    Customer: false,
    Lead: false,
    Opportunity: false,
    activities: false
  });
  
  // Entity selection modal state
  const [entitySelectionModal, setEntitySelectionModal] = useState({
    isOpen: false,
    entityType: null,
    searchTerm: '',
    results: [],
    loading: false,
    selectedIds: [] // Track selected entity IDs in modal
  });
  
  // Debounce timers
  const [searchTimeouts, setSearchTimeouts] = useState({});
  
  // Keyboard navigation state
  const [highlightedIndex, setHighlightedIndex] = useState({
    Customer: -1,
    Lead: -1,
    Opportunity: -1
  });


  // Keep initial relation keys to avoid re-fetch on save
  const initialRelationKeysRef = useRef(new Set());
  
  // Salesperson and data governance state
  const [salespersons, setSalespersons] = useState([]);
  const [dataGovernancePolicy, setDataGovernancePolicy] = useState(null);
  const [showSalespersonWarning, setShowSalespersonWarning] = useState(false);
  const [originalSalespersonID, setOriginalSalespersonID] = useState(null);

  const activityTypes = [
    { value: 'Call', label: t('activities.types.call') },
    { value: 'Meeting', label: t('activities.types.meeting') },
    { value: 'Email', label: t('activities.types.email') },
    { value: 'Task', label: t('activities.types.task') },
    { value: 'Follow-up', label: t('activities.types.followUp') },
    { value: 'Demo', label: t('activities.types.demo') },
    { value: 'Proposal', label: t('activities.types.proposal') },
    { value: 'Contract Review', label: t('activities.types.contractReview') },
    { value: 'Negotiation', label: t('activities.types.negotiation') },
    { value: 'Training', label: t('activities.types.training') },
    { value: 'Support', label: t('activities.types.support') },
    { value: 'Other', label: t('activities.types.other') }
  ];

  const entityTypes = [
    { value: 'Customer', label: t('activities.entityTypes.customer') },
    { value: 'Lead', label: t('activities.entityTypes.lead') },
    { value: 'Opportunity', label: t('activities.entityTypes.opportunity') },
    { value: 'SaleNote', label: t('activities.entityTypes.saleNote', 'Sale Note') }
  ];

  // Initialize form with activity data if editing, or initialData if creating
  useEffect(() => {
    if (activity && activity.ActivityID) {
      console.log('🔄 ActivityForm: Initializing form with activity data:', {
        activityId: activity.ActivityID,
        hasRelatedEntities: !!activity.relatedEntities,
        relatedEntitiesCount: activity.relatedEntities?.length || 0
      });
      
      // Load related entities via entityRelationService
      const loadRelatedEntities = async () => {
        try {
          console.log('🔄 ActivityForm: Loading related entities for activity:', activity.ActivityID);
          const relations = await entityRelationService.getRelatedEntities(
            'Activity',
            activity.ActivityID,
            { direction: 'both' }
          );
          
          console.log('🔄 ActivityForm: Received relations response:', relations);
          
          const relatedEntities = (relations.relations || []).map(rel => {
            // Determine which entity is the related one (not Activity)
            const entityType = rel.SourceEntityType === 'Activity' 
              ? rel.TargetEntityType 
              : rel.SourceEntityType;
            const entityId = rel.SourceEntityType === 'Activity' 
              ? rel.TargetEntityID 
              : rel.SourceEntityID;
            
            return {
              type: entityType,
              id: entityId,
              name: '' // Will be fetched separately if needed
            };
          });

          // Store initial relation keys for submit diff (no blocking fetch later)
          initialRelationKeysRef.current = new Set(
            relatedEntities.map(e => `${e.type}:${e.id}`)
          );
          
          // Fetch entity names for display
          const enrichedEntities = await Promise.all(
            relatedEntities.map(async (entity) => {
              try {
                let name = '';
                switch (entity.type) {
                  case 'Customer':
                    const custRes = await fetch(`/api/customers/${entity.id}`, {
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (custRes.ok) {
                      const custData = await custRes.json();
                      const cust = custData.customer || custData;
                      name = cust.CustomerType === 'Person' 
                        ? `${cust.FirstName || ''} ${cust.LastName || ''}`.trim() 
                        : (cust.CompanyName || `Customer ${entity.id}`);
                    }
                    break;
                  case 'Lead': {
                    const selectedRole = localStorage.getItem('selectedRole');
                    const leadRes = await fetch(`/api/leads/${entity.id}`, {
                      headers: { 
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
                      }
                    });
                    if (leadRes.ok) {
                      const leadData = await leadRes.json();
                      const lead = leadData.lead || leadData;
                      name = lead.Title || lead.ContactName || lead.CompanyName || `Lead ${entity.id}`;
                    }
                    break;
                  }
                  case 'Opportunity': {
                    const selectedRole = localStorage.getItem('selectedRole');
                    const oppRes = await fetch(`/api/opportunities/${entity.id}`, {
                      headers: { 
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
                      }
                    });
                    if (oppRes.ok) {
                      const oppData = await oppRes.json();
                      const opp = oppData.opportunity || oppData;
                      name = opp.OpportunityName || `Opportunity ${entity.id}`;
                    }
                    break;
                  }
                  case 'SaleNote':
                    const saleNoteRes = await fetch(`/api/sales-notes/${entity.id}`, {
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (saleNoteRes.ok) {
                      const saleNoteData = await saleNoteRes.json();
                      const saleNote = saleNoteData.saleNote || saleNoteData.salesNote || saleNoteData;
                      const noteText = saleNote.NoteText || '';
                      const firstLine = noteText.split('\n')[0] || noteText;
                      const previewText = firstLine.length > 100 
                        ? firstLine.substring(0, 100).trim() + '...' 
                        : firstLine.trim();
                      name = previewText || `Sale Note ${entity.id}`;
                    }
                    break;
                  default:
                    name = `${entity.type} ${entity.id}`;
                }
                return { ...entity, name };
              } catch (e) {
                console.warn(`Failed to fetch ${entity.type} ${entity.id}:`, e);
                return { ...entity, name: `${entity.type} ${entity.id}` };
              }
            })
          );
      
      // Set up entity search terms to display selected entities
      const entitySearchTerms = {
        Customer: '',
        Lead: '',
        Opportunity: '',
        SaleNote: ''
      };
          
          enrichedEntities.forEach(entity => {
            if (entitySearchTerms.hasOwnProperty(entity.type)) {
              entitySearchTerms[entity.type] = entity.name;
            }
          });
          
          const selectedEntityTypes = [...new Set(enrichedEntities.map(e => e.type))];
          
          console.log('🔄 ActivityForm: Loaded related entities via entityRelationService:', {
            relationsCount: relations.relations?.length || 0,
            relatedEntitiesCount: relatedEntities.length,
            enrichedEntitiesCount: enrichedEntities.length,
            enrichedEntities,
            entitySearchTerms,
            selectedEntityTypes
          });
          
          setFormData(prev => ({
            ...prev,
            relatedEntities: enrichedEntities,
            selectedEntityTypes
          }));
          setEntitySearchTerms(entitySearchTerms);
        } catch (error) {
          console.error('Failed to load related entities:', error);
          // Fallback to activity.relatedEntities if available
          if (activity.relatedEntities && Array.isArray(activity.relatedEntities)) {
            const relatedEntities = activity.relatedEntities.map(entity => ({
              type: entity.EntityType,
              id: entity.EntityID,
              name: entity.EntityName || ''
            }));
            
            const entitySearchTerms = {
              Customer: '',
              Lead: '',
              Opportunity: '',
              SaleNote: ''
            };
            
            relatedEntities.forEach(entity => {
              if (entitySearchTerms.hasOwnProperty(entity.type)) {
                entitySearchTerms[entity.type] = entity.name;
              }
            });
            
            const selectedEntityTypes = [...new Set(relatedEntities.map(e => e.type))];
            
            setFormData(prev => ({
              ...prev,
              relatedEntities,
              selectedEntityTypes
            }));
            setEntitySearchTerms(entitySearchTerms);
          }
        }
      };
      
      loadRelatedEntities();
      
      // Set up parent activity search term
      let parentSearchTerm = '';
      if (activity.ParentActivityID && activity.ParentSubject) {
        parentSearchTerm = activity.ParentSubject;
      }
      
      const newFormData = {
        activityType: activity.ActivityType || '',
        subject: activity.Subject || '',
        description: activity.Description || '',
        priority: activity.Priority || 'Medium',
        status: activity.Status || 'Planned',
        selectedEntityTypes: [],
        relatedEntities: [],
        dueDate: formatDateForInput(activity.DueDate),
        startDate: formatDateForInput(activity.StartDate),
        endDate: formatDateForInput(activity.EndDate),
        followUpDate: formatDateForInput(activity.FollowUpDate),
        estimatedDuration: activity.EstimatedDuration || '',
        actualDuration: activity.ActualDuration || '',
        location: activity.Location || '',
        notes: activity.Notes || '',
        parentActivityId: activity.ParentActivityID || '',
        salespersonId: activity.SalespersonID ? String(activity.SalespersonID) : (user?.tenantUserId || user?.userId || user?.UserID ? String(user.tenantUserId || user.userId || user.UserID) : ''),
        outcome: activity.Outcome || ''
      };
      
      // Set original salesperson ID for warning logic
      if (activity.SalespersonID) {
        setOriginalSalespersonID(String(activity.SalespersonID));
      }
      
      console.log('🔄 ActivityForm: Setting initial form data (entities will be loaded async):', newFormData);
      
      // Set form data (related entities will be set by loadRelatedEntities)
      setFormData(newFormData);
      setParentSearchTerm(parentSearchTerm);
    } else {
      // Creating a new activity
      let newFormData = {
        activityType: '',
        subject: '',
        description: '',
        priority: 'Medium',
        status: 'Planned',
        selectedEntityTypes: [],
        relatedEntities: [],
        dueDate: '',
        startDate: '',
        endDate: '',
        followUpDate: '',
        estimatedDuration: '',
        actualDuration: '',
        location: '',
        notes: '',
        parentActivityId: '',
        salespersonId: user?.tenantUserId || user?.userId || user?.UserID ? String(user.tenantUserId || user.userId || user.UserID) : '',
        outcome: ''
      };
      
      if (initialData) {
        // Pre-fill form with initial data when creating from entity detail page
        newFormData = {
          ...newFormData,
          selectedEntityTypes: initialData.relatedEntityType ? [initialData.relatedEntityType] : [],
          relatedEntities: initialData.relatedEntityType && initialData.relatedEntityId ? [{
            type: initialData.relatedEntityType,
            id: initialData.relatedEntityId,
            name: initialData.entityName || ''
          }] : []
        };
        
        // Set the search term to show the entity name
        if (initialData.entityName && initialData.relatedEntityType) {
          setEntitySearchTerms(prev => ({
            ...prev,
            [initialData.relatedEntityType]: initialData.entityName
          }));
        }
      }
      
      setFormData(newFormData);
    }
  }, [activity, initialData, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Force re-initialization when activity changes
  useEffect(() => {
    if (activity && activity.ActivityID) {
      console.log('🔄 ActivityForm: Force re-initialization triggered for activity:', activity.ActivityID);
      
      // Force a complete re-initialization
      const relatedEntities = activity.relatedEntities && Array.isArray(activity.relatedEntities) 
        ? activity.relatedEntities.map(entity => ({
          type: entity.EntityType,
          id: entity.EntityID,
          name: entity.EntityName || ''
        }))
        : [];
      
      const entitySearchTerms = {
        Customer: '',
        Lead: '',
        Opportunity: '',
        SaleNote: ''
      };
      
      if (activity.relatedEntities && Array.isArray(activity.relatedEntities)) {
        activity.relatedEntities.forEach(entity => {
          entitySearchTerms[entity.EntityType] = entity.EntityName || '';
        });
      }
      
      const selectedEntityTypes = activity.relatedEntities && Array.isArray(activity.relatedEntities) 
        ? [...new Set(activity.relatedEntities.map(entity => entity.EntityType))]
        : [];
      
      console.log('🔄 ActivityForm: Force re-init - related entities:', relatedEntities);
      console.log('🔄 ActivityForm: Force re-init - entity search terms:', entitySearchTerms);
      console.log('🔄 ActivityForm: Force re-init - selected entity types:', selectedEntityTypes);
      
      // Force update the state
      setFormData(prev => ({
        ...prev,
        relatedEntities,
        selectedEntityTypes
      }));
      
      setEntitySearchTerms(entitySearchTerms);
    }
  }, [activity?.ActivityID]); // Only trigger when ActivityID changes



    // Comprehensive effect to synchronize entitySearchTerms with formData.relatedEntities
    // Only run this effect when relatedEntities change due to user actions, not during initialization
  useEffect(() => {
    // Skip if we're in the middle of initialization or if relatedEntities is empty
    if (!formData.relatedEntities || formData.relatedEntities.length === 0) {
      return;
    }
    
    // Only update search terms if they don't already match
    const newEntitySearchTerms = { ...entitySearchTerms };
    const newSelectedEntityTypes = [...formData.selectedEntityTypes];
    let hasSearchTermChanges = false;
    let hasEntityTypeChanges = false;
    
    formData.relatedEntities.forEach(entity => {
      // Only update search terms if they're empty or different
      if (!entitySearchTerms[entity.type] || entitySearchTerms[entity.type] !== entity.name) {
        newEntitySearchTerms[entity.type] = entity.name;
        hasSearchTermChanges = true;
      }
      
      // Ensure entity type is selected
      if (!newSelectedEntityTypes.includes(entity.type)) {
        newSelectedEntityTypes.push(entity.type);
        hasEntityTypeChanges = true;
      }
    });
    
    // Only update if there are actual changes to prevent unnecessary re-renders
    if (hasSearchTermChanges) {
      console.log('🔄 ActivityForm: Updating entity search terms:', newEntitySearchTerms);
      setEntitySearchTerms(newEntitySearchTerms);
    }
    
    if (hasEntityTypeChanges) {
      console.log('🔄 ActivityForm: Updating selected entity types:', newSelectedEntityTypes);
      setFormData(prev => ({
        ...prev,
        selectedEntityTypes: newSelectedEntityTypes
      }));
    }
  }, [formData.relatedEntities]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-dropdown')) {
        setShowEntitySearch({ Customer: false, Lead: false, Opportunity: false, SaleNote: false });
        setShowParentSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(searchTimeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [searchTimeouts]);

  // Fetch salespersons and data governance policy
  useEffect(() => {
    const fetchSalespersons = async () => {
      try {
        const selectedRole = localStorage.getItem('selectedRole');
        const response = await fetch('/api/users/salespersons', {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });
        if (response.ok) {
          const data = await response.json();
          // Filter to only include users with Salesperson role (exclude Admin-only users)
          const salespersonOnly = (data.salespersons || []).filter(user => {
            const roles = user.Roles || '';
            // Include if user has Salesperson role (may also have Admin, but must have Salesperson)
            return roles.includes('Salesperson');
          });
          setSalespersons(salespersonOnly);
        }
      } catch (error) {
        console.error('Error fetching salespersons:', error);
      }
    };
    
    const fetchDataGovernancePolicy = async () => {
      try {
        const response = await dataGovernanceService.getPolicy('Activity');
        // Handle different response structures
        const policy = response?.policy || response?.data || response;
        setDataGovernancePolicy(policy);
        console.log('🔍 ActivityForm: Data governance policy loaded:', policy);
      } catch (error) {
        console.error('Error fetching data governance policy:', error);
      }
    };
    
    fetchSalespersons();
    fetchDataGovernancePolicy();
  }, []);

  // Load dynamic list values from entity config
  useEffect(() => {
    if (!entityConfig) return; // Wait for entity config to load
    
    const loadListValues = async () => {
      try {
        console.log('🔍 ActivityForm: Loading list values from entity config...');
        // The entityConfig returned from useEntityConfig hook is already the config object
          
          console.log('🔍 ActivityForm: Received config:', {
            hasActivityType: !!entityConfig?.fields?.ActivityType,
            activityTypeOptions: entityConfig?.fields?.ActivityType?.ui?.options,
            hasPriority: !!entityConfig?.fields?.Priority,
            priorityOptions: entityConfig?.fields?.Priority?.ui?.options,
            hasStatus: !!entityConfig?.fields?.Status,
            statusOptions: entityConfig?.fields?.Status?.ui?.options
          });
          
          // Update ActivityType options if available in config
          if (entityConfig?.fields?.ActivityType?.ui?.options) {
            setActivityTypeOptions(entityConfig.fields.ActivityType.ui.options);
          } else {
            // Set default options if config not available
            setActivityTypeOptions(activityTypes);
          }
          
          // Update Priority options if available in config
          if (entityConfig?.fields?.Priority?.ui?.options) {
            setPriorityOptions(entityConfig.fields.Priority.ui.options);
          }
          
          // Update Status options if available in config
          if (entityConfig?.fields?.Status?.ui?.options) {
            setStatusOptions(entityConfig.fields.Status.ui.options);
          }
      } catch (error) {
        console.error('❌ ActivityForm: Failed to load list values:', error);
        // Keep using default options if loading fails
        setActivityTypeOptions(activityTypes);
      }
    };
    
    loadListValues();
  }, [entityConfig]);

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Check for salesperson change warning
    if (field === 'salespersonId' && activity && originalSalespersonID) {
      const newSalespersonId = String(value || '');
      const hasChanged = newSalespersonId !== originalSalespersonID && newSalespersonId !== '';
      const currentUserId = String(user?.tenantUserId || user?.userId || user?.UserID || '');
      
      // Show warning if policy is 1 or 2 and user is changing salesperson to someone else
      if (hasChanged && dataGovernancePolicy) {
        const policyType = Number(dataGovernancePolicy.policyType || dataGovernancePolicy.PolicyType || 0);
        const isPolicy1Or2 = policyType === 1 || policyType === 2;
        const isChangingToOther = newSalespersonId !== currentUserId;
        
        console.log('🔍 ActivityForm: Salesperson change check:', {
          originalSalespersonID,
          newSalespersonId,
          currentUserId,
          policyType,
          isPolicy1Or2,
          isChangingToOther,
          dataGovernancePolicy
        });
        
        if (isPolicy1Or2 && isChangingToOther) {
          setShowSalespersonWarning(true);
        } else {
          setShowSalespersonWarning(false);
        }
      } else {
        setShowSalespersonWarning(false);
      }
    } else if (field === 'salespersonId' && activity && !originalSalespersonID) {
      // If no original salesperson, set it when first loaded
      const newSalespersonId = String(value || '');
      if (newSalespersonId && !originalSalespersonID) {
        setOriginalSalespersonID(newSalespersonId);
      }
    }
  };

  // Handle entity type checkbox changes
  const handleEntityTypeChange = (entityType, checked) => {
    setFormData(prev => {
      const newSelectedEntityTypes = checked
        ? [...prev.selectedEntityTypes, entityType]
        : prev.selectedEntityTypes.filter(type => type !== entityType);
      
      // Remove related entities of unchecked type
      const newRelatedEntities = checked
        ? prev.relatedEntities
        : prev.relatedEntities.filter(entity => entity.type !== entityType);
      
      return {
        ...prev,
        selectedEntityTypes: newSelectedEntityTypes,
        relatedEntities: newRelatedEntities
      };
    });

    // Clear search term if unchecked
    if (!checked) {
      setEntitySearchTerms(prev => ({
        ...prev,
        [entityType]: ''
      }));
    }
  };

  // Debounced search for entities (customers, leads, opportunities)
  const searchEntities = (term, entityType) => {
    // Clear existing timeout
    if (searchTimeouts[entityType]) {
      clearTimeout(searchTimeouts[entityType]);
    }

    // For customers, show preview even with empty term
    if (entityType === 'Customer' && (!term || term.trim() === '')) {
      setShowEntitySearch(prev => ({
        ...prev,
        [entityType]: true
      }));
      setSearchLoading(prev => ({ ...prev, [entityType]: true }));
      
      // Get preview immediately
      performEntitySearch('', entityType);
      return;
    }

    if (!term || term.length < 1) {
      setSearchResults(prev => ({
        ...prev,
        [entityType.toLowerCase() + 's']: []
      }));
      setShowEntitySearch(prev => ({
        ...prev,
        [entityType]: false
      }));
      setHighlightedIndex(prev => ({
        ...prev,
        [entityType]: -1
      }));
      return;
    }

    // Show dropdown immediately for better UX
    setShowEntitySearch(prev => ({
      ...prev,
      [entityType]: true
    }));

    // For single character, don't search yet but show dropdown
    if (term.length < 2) {
      setSearchResults(prev => ({
        ...prev,
        [entityType.toLowerCase() + 's']: []
      }));
      setHighlightedIndex(prev => ({
        ...prev,
        [entityType]: -1
      }));
      return;
    }

    setSearchLoading(prev => ({ ...prev, [entityType]: true }));

    // Set new timeout - reduced for more responsive feel
    const timeoutId = setTimeout(() => {
      performEntitySearch(term, entityType);
    }, 200);

    setSearchTimeouts(prev => ({
      ...prev,
      [entityType]: timeoutId
    }));
  };

  // Open entity selection modal
  const openEntitySelectionModal = (entityType, currentSearchTerm = '') => {
    console.log('Opening entity selection modal for:', entityType, 'with search term:', currentSearchTerm);
    
    // Get already selected entity IDs for this type
    const alreadySelectedIds = formData.relatedEntities
      .filter(e => e.type === entityType)
      .map(e => e.id.toString());
    
    setEntitySelectionModal({
      isOpen: true,
      entityType,
      searchTerm: currentSearchTerm,
      results: [],
      loading: true,
      selectedIds: alreadySelectedIds
    });
    
    // Always perform initial search to show available entities
    performEntitySearchForModal(currentSearchTerm || '', entityType);
  };

  // Toggle entity selection in modal
  const toggleEntitySelectionInModal = (entity, entityType) => {
    const idStr = getEntityId(entity, entityType)?.toString();
    if (!idStr) return;
    setEntitySelectionModal(prev => {
      const isSelected = prev.selectedIds.includes(idStr);
      return { ...prev, selectedIds: isSelected ? prev.selectedIds.filter(x => x !== idStr) : [...prev.selectedIds, idStr] };
    });
  };

  // Close entity selection modal and add selected entities
  const closeEntitySelectionModal = (addSelected = false) => {
    if (addSelected && entitySelectionModal.entityType) {
      // Add all selected entities that aren't already in formData
      const newEntities = [];
      
      entitySelectionModal.results.forEach(entity => {
        const entityId = getEntityId(entity, entitySelectionModal.entityType);
        const entityIdStr = entityId.toString();
        
        // Check if this entity is selected in modal and not already in formData
        if (entitySelectionModal.selectedIds.includes(entityIdStr)) {
          const isAlreadyAdded = formData.relatedEntities.some(
            e => e.type === entitySelectionModal.entityType && e.id.toString() === entityIdStr
          );
          
          if (!isAlreadyAdded) {
            const entityName = getEntityName(entity, entitySelectionModal.entityType);
            newEntities.push({
              type: entitySelectionModal.entityType,
              id: entityId,
              name: entityName
            });
          }
        }
      });
      
      // Remove entities that were deselected in modal
      const remainingIds = new Set(entitySelectionModal.selectedIds);
      const updatedRelatedEntities = formData.relatedEntities.filter(e => {
        if (e.type === entitySelectionModal.entityType) {
          return remainingIds.has(e.id.toString());
        }
        return true;
      });
      
      // Add new entities
      setFormData(prev => ({
        ...prev,
        relatedEntities: [...updatedRelatedEntities, ...newEntities]
      }));
    }
    
    setEntitySelectionModal({
      isOpen: false,
      entityType: null,
      searchTerm: '',
      results: [],
      loading: false,
      selectedIds: []
    });
  };

  // Helper to get entity ID
  const getEntityId = (entity, entityType) => {
    switch (entityType) {
      case 'Customer': return entity.CustomerID;
      case 'Lead': return entity.LeadID;
      case 'Opportunity': return entity.OpportunityID;
      case 'SaleNote': return entity.SaleNoteID || entity.NoteID;
      default: return null;
    }
  };

  // Helper to get entity name
  const getEntityName = (entity, entityType) => {
    switch (entityType) {
      case 'Customer': {
        const personName = (entity.CustomerName && entity.CustomerName.trim())
          || `${(entity.FirstName || '').trim()} ${(entity.LastName || '').trim()}`.trim();
        return personName || entity.CompanyName || `Customer ${entity.CustomerID}`;
      }
      case 'Lead': return entity.Title || entity.ContactName || entity.CompanyName || `Lead ${entity.LeadID}`;
      case 'Opportunity': return entity.OpportunityName || entity.Title || `Opportunity ${entity.OpportunityID}`;
      case 'SaleNote': {
        const noteText = entity.NoteText || '';
        const firstLine = noteText.split('\n')[0] || noteText;
        const previewText = firstLine.length > 100 
          ? firstLine.substring(0, 100).trim() + '...' 
          : firstLine.trim();
        return previewText || `Sale Note ${entity.NoteID || entity.SaleNoteID}`;
      }
      default: return `${entityType} ${getEntityId(entity, entityType)}`;
    }
  };

  // Get entity sub-info
  const getEntitySubInfo = (entity, entityType) => {
    switch (entityType) {
      case 'Customer':
        return entity.CompanyName || entity.Email || entity.Phone || '';
      case 'Lead':
        return entity.CompanyName || entity.ContactEmail || entity.ContactPhone || '';
      case 'Opportunity':
        return entity.Stage || '';
      case 'SaleNote':
        if (entity.NoteDate) {
          try {
            return new Date(entity.NoteDate).toLocaleDateString();
          } catch (_) {
            return '';
          }
        }
        return '';
      default:
        return '';
    }
  };

  // Search entities for modal
  const performEntitySearchForModal = async (term, entityType) => {
    try {
      let endpoint = '';
      switch (entityType) {
        case 'Customer':
          if (!term || term.trim() === '') {
            endpoint = `/api/customers?limit=10`;
          } else {
            endpoint = `/api/customers?search=${encodeURIComponent(term)}&limit=15`;
          }
          break;
        case 'Lead':
          if (!term || term.trim() === '') {
            endpoint = `/api/leads/for-assignment?limit=10`;
          } else {
            endpoint = `/api/leads/for-assignment?search=${encodeURIComponent(term)}&limit=15`;
          }
          break;
        case 'Opportunity':
          if (!term || term.trim() === '') {
            endpoint = `/api/opportunities/for-assignment?limit=10`;
          } else {
            endpoint = `/api/opportunities/for-assignment?search=${encodeURIComponent(term)}&limit=15`;
          }
          break;
        case 'SaleNote':
          if (!term || term.trim() === '') {
            endpoint = `/api/sales-notes?limit=10`;
          } else {
            endpoint = `/api/sales-notes?search=${encodeURIComponent(term)}&limit=15`;
          }
          break;
        default:
          console.warn(`Unsupported entity type for search: ${entityType}`);
          return;
      }

      console.log(`Searching ${entityType} for modal with endpoint:`, endpoint);

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      console.log(`${entityType} modal search response status:`, response.status);

      if (response.ok) {
        const data = await response.json();
        console.log(`${entityType} modal search response:`, data);
        
        // Handle different response structures
        let results = [];
        const entityKey = entityType.toLowerCase() + 's';
        if (data[entityKey]) {
          results = data[entityKey];
        } else if (data.saleNotes && Array.isArray(data.saleNotes)) {
          results = data.saleNotes;
        } else if (data.salesNotes && Array.isArray(data.salesNotes)) {
          results = data.salesNotes;
        } else if (data.opportunities && Array.isArray(data.opportunities)) {
          results = data.opportunities;
        } else if (data.leads && Array.isArray(data.leads)) {
          results = data.leads;
        } else if (data.data && Array.isArray(data.data)) {
          results = data.data;
        } else if (Array.isArray(data)) {
          results = data;
        }
        
        console.log(`${entityType} modal search results:`, results);
        
        setEntitySelectionModal(prev => ({
          ...prev,
          results,
          loading: false
        }));
      } else {
        console.error(`${entityType} modal search failed:`, response.status, response.statusText);
        // Try to get error details
        try {
          const errorData = await response.json();
          console.error('Error details:', errorData);
        } catch (e) {
          console.error('Could not parse error response');
        }
        setEntitySelectionModal(prev => ({
          ...prev,
          results: [],
          loading: false
        }));
      }
    } catch (err) {
      console.error(`${entityType} modal search failed:`, err);
      setEntitySelectionModal(prev => ({
        ...prev,
        results: [],
        loading: false
      }));
    }
  };

  // Handle entity selection from modal
  const handleEntitySelectFromModal = null;

  // Actual search function
  const performEntitySearch = async (term, entityType) => {
    try {
      let endpoint = '';
      switch (entityType) {
        case 'Customer':
          // For customers, if no search term, get recent customers for preview
          if (!term || term.trim() === '') {
            endpoint = `/api/customers?limit=10`;
          } else {
            endpoint = `/api/customers?search=${encodeURIComponent(term)}&limit=15`;
          }
          break;
        case 'Lead':
          endpoint = `/api/leads?search=${encodeURIComponent(term)}&limit=15`;
          break;
        case 'Opportunity':
          endpoint = `/api/opportunities?search=${encodeURIComponent(term)}&limit=15`;
          break;
        default:
          return;
      }

      console.log(`Searching ${entityType} with endpoint:`, endpoint);

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`${entityType} search response:`, data);
        
        // Handle different response structures
        let results = [];
        if (data[entityType.toLowerCase() + 's']) {
          results = data[entityType.toLowerCase() + 's'];
        } else if (data.data && Array.isArray(data.data)) {
          results = data.data;
        } else if (Array.isArray(data)) {
          results = data;
        }
        
        setSearchResults(prev => ({
          ...prev,
          [entityType.toLowerCase() + 's']: results
        }));
        
        // Reset highlighted index when new results come in
        setHighlightedIndex(prev => ({
          ...prev,
          [entityType]: -1
        }));
      } else {
        console.error(`${entityType} search failed:`, response.status, response.statusText);
        // Try to get error message from response
        try {
          const errorData = await response.json();
          console.error('Error details:', errorData);
        } catch (e) {
          // Ignore JSON parse errors
        }
        setSearchResults(prev => ({
          ...prev,
          [entityType.toLowerCase() + 's']: []
        }));
      }
    } catch (err) {
      console.error(`${entityType} search failed:`, err);
      setSearchResults(prev => ({
        ...prev,
        [entityType.toLowerCase() + 's']: []
      }));
    } finally {
      setSearchLoading(prev => ({ ...prev, [entityType]: false }));
    }
  };

  // Debounced search for parent activities
  const searchParentActivities = (term) => {
    // Clear existing timeout
    if (searchTimeouts.activities) {
      clearTimeout(searchTimeouts.activities);
    }

    if (!term || term.length < 2) {
      setSearchResults(prev => ({
        ...prev,
        activities: []
      }));
      return;
    }

    setSearchLoading(prev => ({ ...prev, activities: true }));

    // Set new timeout
    const timeoutId = setTimeout(() => {
      performActivitiesSearch(term);
    }, 300);

    setSearchTimeouts(prev => ({
      ...prev,
      activities: timeoutId
    }));
  };

  // Actual search function
  const performActivitiesSearch = async (term) => {

    try {
      const endpoint = `/api/activities?search=${encodeURIComponent(term)}&limit=10`;
      console.log('Searching activities with endpoint:', endpoint);

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Activities search response:', data);
        
        setSearchResults(prev => ({
          ...prev,
          activities: data.activities || []
        }));
      } else {
        console.error('Activities search failed:', response.status, response.statusText);
        setSearchResults(prev => ({
          ...prev,
          activities: []
        }));
      }
    } catch (err) {
      console.error('Parent activity search failed:', err);
      setSearchResults(prev => ({
        ...prev,
        activities: []
      }));
    } finally {
      setSearchLoading(prev => ({ ...prev, activities: false }));
    }
  };


  // Handle entity selection
  const handleEntitySelect = (entity, entityType) => {
    let entityId = '';
    let entityName = '';

    switch (entityType) {
      case 'Customer':
        entityId = entity.CustomerID;
        // Prefer person/customer name first, fallback to company name
        {
          const personName = (entity.CustomerName && entity.CustomerName.trim())
            || `${(entity.FirstName || '').trim()} ${(entity.LastName || '').trim()}`.trim();
          if (personName) {
            entityName = personName;
          } else if (entity.CompanyName) {
            entityName = entity.CompanyName;
          } else {
            entityName = `Customer ${entity.CustomerID}`;
          }
        }
        break;
      case 'Lead':
        entityId = entity.LeadID;
        entityName = entity.ContactName || entity.CompanyName || entity.Title;
        break;
      case 'Opportunity':
        entityId = entity.OpportunityID;
        entityName = entity.Title || entity.OpportunityName || entity.CustomerName;
        break;
      default:
        return;
    }

    // Check if entity is already selected
    const isAlreadySelected = formData.relatedEntities.some(
      e => e.type === entityType && e.id === entityId
    );

    if (!isAlreadySelected) {
      setFormData(prev => ({
        ...prev,
        relatedEntities: [...prev.relatedEntities, {
          type: entityType,
          id: entityId,
          name: entityName
        }]
      }));
    }

    setEntitySearchTerms(prev => ({
      ...prev,
      [entityType]: entityName
    }));
    setShowEntitySearch(prev => ({
      ...prev,
      [entityType]: false
    }));
  };

  // Remove selected entity
  const removeEntity = (entityType, entityId) => {
    setFormData(prev => ({
      ...prev,
      relatedEntities: prev.relatedEntities.filter(
        e => !(e.type === entityType && e.id === entityId)
      )
    }));
  };

  // Handle parent activity selection
  const handleParentSelect = (parentActivity) => {
    handleChange('parentActivityId', parentActivity.ActivityID);
    setParentSearchTerm(parentActivity.Subject);
    setShowParentSearch(false);
  };


  // Handle form submission
  // Validate form fields
  const validateForm = () => {
    const errs = {};
    
    // Activity type is required
    if (isEmpty(formData.activityType)) {
      errs.activityType = 'กรุณาเลือกประเภทกิจกรรม / Please select activity type';
    }
    
    // Subject is required
    if (isEmpty(formData.subject)) {
      errs.subject = 'กรุณากรอกเรื่อง / Please enter subject';
    }
    
    // Status-based date validations
    if (formData.status === 'Planned' && isEmpty(formData.dueDate)) {
      errs.dueDate = 'กรุณากรอกวันครบกำหนด / Due date is required for Planned status';
    }
    
    if (formData.status === 'In Progress' && isEmpty(formData.startDate)) {
      errs.startDate = 'กรุณากรอกวันเริ่มต้น / Start date is required for In Progress status';
    }
    
    if (formData.status === 'Done' && isEmpty(formData.startDate)) {
      errs.startDate = 'กรุณากรอกวันเริ่มต้น / Start date is required for completed activities';
    }
    
    // Duration validation
    if (!isEmpty(formData.estimatedDuration) && Number(formData.estimatedDuration) < 0) {
      errs.estimatedDuration = 'ระยะเวลาต้องเป็นตัวเลขบวก / Duration must be a positive number';
    }
    
    if (!isEmpty(formData.actualDuration) && Number(formData.actualDuration) < 0) {
      errs.actualDuration = 'ระยะเวลาต้องเป็นตัวเลขบวก / Duration must be a positive number';
    }
    
    setFieldErrors(errs);
    
    if (Object.keys(errs).length > 0) {
      scrollToFirstError(errs);
    }
    
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form first
    if (!validateForm()) {
      return;
    }
    
    console.log('🚀 ActivityForm: Submit started', {
      isEdit: !!activity,
      activityId: activity?.ActivityID,
      relatedEntitiesCount: formData.relatedEntities?.length || 0,
      relatedEntities: formData.relatedEntities
    });
    
    setLoading(true);
    setError(null);

    try {
      console.log('📝 ActivityForm: Step 1 - Preparing form data...');
      
      // Helper function to convert date to ISO8601 format
      const formatDateToISO = (dateString) => {
        if (!dateString) return null;
        // Convert YYYY-MM-DD to ISO8601 format (YYYY-MM-DDTHH:mm:ss.sssZ)
        return new Date(dateString + 'T00:00:00.000Z').toISOString();
      };

      // Helper function to convert string to integer or null
      const parseIntOrNull = (value) => {
        if (!value || value === '') return null;
        const parsed = parseInt(value);
        return isNaN(parsed) ? null : parsed;
      };

      // Prepare data for submission - only include fields with actual values
      const submitData = {
        ActivityType: formData.activityType,
        Subject: formData.subject,
        Description: formData.description,
        Priority: formData.priority,
        Status: formData.status
      };
      
      console.log('📝 ActivityForm: Step 1.1 - Base submitData:', submitData);

      // Handle date fields based on status requirements
      if (formData.status === 'Planned') {
        // Due date is required for Planned status
        const dueDate = formatDateToISO(formData.dueDate);
        if (!dueDate) {
          setError(t('activities.validation.dueDateRequired'));
          setLoading(false);
          return;
        }
        submitData.DueDate = dueDate;
        
        // Follow-up date is optional
        const followUpDate = formatDateToISO(formData.followUpDate);
        if (followUpDate) submitData.FollowUpDate = followUpDate;
      } else if (formData.status === 'In Progress') {
        // Start date is required for In Progress status
        const startDate = formatDateToISO(formData.startDate);
        if (!startDate) {
          setError(t('activities.validation.startDateRequired'));
          setLoading(false);
          return;
        }
        submitData.StartDate = startDate;
        
        // Follow-up date is optional
        const followUpDate = formatDateToISO(formData.followUpDate);
        if (followUpDate) submitData.FollowUpDate = followUpDate;
      } else if (formData.status === 'Done') {
        // Start date is required for Done status
        const startDate = formatDateToISO(formData.startDate);
        if (!startDate) {
          setError(t('activities.validation.startDateRequiredCompleted'));
          setLoading(false);
          return;
        }
        submitData.StartDate = startDate;
        
        // End date defaults to start date if not provided
        const endDate = formatDateToISO(formData.endDate);
        submitData.EndDate = endDate || startDate;
      } else {
        // For other statuses, all dates are optional
        const dueDate = formatDateToISO(formData.dueDate);
        if (dueDate) submitData.DueDate = dueDate;

        const startDate = formatDateToISO(formData.startDate);
        if (startDate) submitData.StartDate = startDate;

        const endDate = formatDateToISO(formData.endDate);
        if (endDate) submitData.EndDate = endDate;

        const followUpDate = formatDateToISO(formData.followUpDate);
        if (followUpDate) submitData.FollowUpDate = followUpDate;
      }

      const estimatedDuration = parseIntOrNull(formData.estimatedDuration);
      if (estimatedDuration !== null) submitData.EstimatedDuration = estimatedDuration;

      const actualDuration = parseIntOrNull(formData.actualDuration);
      if (actualDuration !== null) submitData.ActualDuration = actualDuration;

      if (formData.location) submitData.Location = formData.location;
      if (formData.notes) submitData.Notes = formData.notes;

      const parentActivityID = parseIntOrNull(formData.parentActivityId);
      if (parentActivityID !== null) submitData.ParentActivityID = parentActivityID;

      // SalespersonID is required for Activities
      const salespersonId = parseIntOrNull(formData.salespersonId);
      if (salespersonId !== null) {
        submitData.SalespersonID = salespersonId;
      } else if (user?.tenantUserId || user?.userId || user?.UserID) {
        // Fallback to current user if not set
        submitData.SalespersonID = parseInt(user.tenantUserId || user.userId || user.UserID);
      }

      if (formData.outcome) submitData.Outcome = formData.outcome;

      // Don't send RelatedEntities in submitData - we'll handle relationships via entityRelationService
      
      console.log('📝 ActivityForm: Step 1.2 - Final submitData prepared:', {
        submitData,
        relatedEntitiesCount: formData.relatedEntities?.length || 0,
        relatedEntities: formData.relatedEntities
      });

      const url = activity ? `/api/activities/${activity.ActivityID}` : '/api/activities';
      const method = activity ? 'PUT' : 'POST';

      // Get tenant code for tenant-aware submission
      const tenantCode = getProtectedTenantCode();
      
      console.log('🌐 ActivityForm: Step 2 - Making API request...', {
        url,
        method,
        tenantCode,
        hasRelatedEntities: (formData.relatedEntities?.length || 0) > 0
      });
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        },
        body: JSON.stringify(submitData)
      });

      console.log('🌐 ActivityForm: Step 2.1 - API response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log('✅ ActivityForm: Step 2.2 - Activity saved successfully:', responseData);
        toast.success(t('activities.saveSuccess', 'Activity saved successfully'));
        
        const activityId = activity ? activity.ActivityID : (responseData.activity?.ActivityID || responseData.ActivityID || responseData.id || responseData.activityId);

        // Convert activityId to number if it's a string
        const numericActivityId = typeof activityId === 'string' ? parseInt(activityId, 10) : activityId;
        
        console.log('🔍 ActivityForm: Step 3 - Activity ID extracted:', {
          isEdit: !!activity,
          activityId,
          activityIdType: typeof activityId,
          numericActivityId,
          numericActivityIdType: typeof numericActivityId,
          isNaN: isNaN(numericActivityId),
          responseData,
          responseDataKeys: Object.keys(responseData),
          relatedEntitiesCount: formData.relatedEntities?.length || 0
        });

        // Save relationships synchronously to ensure they're persisted before closing
        if (numericActivityId && !isNaN(numericActivityId) && (typeof numericActivityId === 'number' || typeof numericActivityId === 'string')) {
          const hasRelatedEntities = (formData.relatedEntities?.length || 0) > 0;
          
          console.log('🔗 ActivityForm: Step 4 - Starting relationship sync...', {
            activityId: numericActivityId,
            relatedEntities: formData.relatedEntities || [],
            relatedEntitiesLength: formData.relatedEntities?.length || 0,
            hasRelatedEntities,
            willSkipFetch: !hasRelatedEntities && (!initialRelationKeysRef.current || initialRelationKeysRef.current.size === 0)
          });
          
          // Skip relationship sync entirely if no entities selected and no cached relations
          const hasCachedRelations = initialRelationKeysRef.current && initialRelationKeysRef.current.size > 0;
          const shouldSkipSync = !hasRelatedEntities && !hasCachedRelations;
          
          console.log('🔗 ActivityForm: Step 4 - Relationship sync decision:', {
            hasRelatedEntities,
            hasCachedRelations,
            shouldSkipSync,
            cacheSize: initialRelationKeysRef.current?.size || 0
          });
          
          if (shouldSkipSync) {
            console.log('⚡ ActivityForm: Step 4 - SKIPPING relationship sync entirely (no entities selected and no cached relations)');
            // Skip to Step 5 directly
          } else {
            try {
              console.log('🔗 ActivityForm: Step 4.1 - Preparing to sync relationships...');
              
              // Helper: timeout wrapper to avoid hang
              const withTimeout = async (promise, ms, label) => {
                let timeoutId;
                const timeoutPromise = new Promise((_, reject) => {
                  timeoutId = setTimeout(() => reject(new Error(`Timeout after ${ms}ms in ${label}`)), ms);
                });
                try {
                  const result = await Promise.race([promise, timeoutPromise]);
                  return result;
                } finally {
                  clearTimeout(timeoutId);
                }
              };

              // Get existing relationships (time-boxed)
              // Prefer initial keys we loaded earlier to avoid a blocking fetch
              let existingRelations;
              if (initialRelationKeysRef.current && initialRelationKeysRef.current.size > 0) {
                const fromCache = Array.from(initialRelationKeysRef.current).map(k => {
                  const [t, i] = k.split(':');
                  return { SourceEntityType: 'Activity', SourceEntityID: numericActivityId, TargetEntityType: t, TargetEntityID: Number(i) };
                });
                existingRelations = { relations: fromCache };
                console.log('🔗 ActivityForm: Step 4.1C - Using cached initial relations:', fromCache);
              } else if (hasRelatedEntities) {
                // Skip fetching existing relations to avoid blocking; we'll just create selected ones
                console.log('⚡ ActivityForm: Step 4.1 - Skipping existing relations fetch; proceeding with create-only');
                existingRelations = { relations: [] };
              } else {
                // No selected entities and no cache - use empty relations
                existingRelations = { relations: [] };
                console.log('🔗 ActivityForm: Step 4.1 - No entities selected, using empty existing relations');
              }
            
            console.log('🔗 ActivityForm: Step 4.2 - Existing relationships retrieved:', {
              existingRelations,
              relationsCount: existingRelations?.relations?.length || 0,
              relations: existingRelations?.relations || []
            });
            
            const existingEntityKeys = new Set(
              (existingRelations.relations || []).map(rel => {
                // Handle different casing
                const srcType = rel.SourceEntityType || rel.sourceEntityType || '';
                const tgtType = rel.TargetEntityType || rel.targetEntityType || '';
                const srcId = rel.SourceEntityID || rel.sourceEntityID;
                const tgtId = rel.TargetEntityID || rel.targetEntityID;
                
                const isActivitySource = srcType.toLowerCase() === 'activity';
                const entityType = isActivitySource ? tgtType : srcType;
                const eId = isActivitySource ? tgtId : srcId;
                return `${entityType}:${eId}`;
              })
            );

            const selectedEntityKeys = new Set((formData.relatedEntities || []).map(e => `${e.type}:${e.id}`));
            
            console.log('🔗 ActivityForm: Step 4.3 - Entity keys comparison:', {
              existingEntityKeys: Array.from(existingEntityKeys),
              selectedEntityKeys: Array.from(selectedEntityKeys),
              toCreate: Array.from(selectedEntityKeys).filter(k => !existingEntityKeys.has(k)),
              toDelete: Array.from(existingEntityKeys).filter(k => !selectedEntityKeys.has(k))
            });

            // Create new relationships that don't exist yet
            const entitiesToCreate = (formData.relatedEntities || [])
              .filter(entity => {
                const entityKey = `${entity.type}:${entity.id}`;
                return !existingEntityKeys.has(entityKey);
              });
            
            console.log('🔗 ActivityForm: Step 4.4 - Creating relationships:', {
              entitiesToCreate,
              count: entitiesToCreate.length
            });
            
            const createPromises = entitiesToCreate.map(async (entity, index) => {
                try {
                  console.log(`🔗 ActivityForm: Step 4.4.${index + 1} - Creating relation for ${entity.type} ${entity.id}...`, {
                    sourceEntityType: 'Activity',
                    sourceEntityID: numericActivityId,
                    targetEntityType: entity.type,
                    targetEntityID: entity.id,
                    relationType: 'related_to'
                  });
                  const result = await withTimeout(
                    entityRelationService.create({
                    sourceEntityType: 'Activity',
                    sourceEntityID: numericActivityId,
                    targetEntityType: entity.type,
                    targetEntityID: entity.id,
                    relationType: 'related_to'
                    }),
                    6000,
                    `createRelation(${entity.type}:${entity.id})`
                  );
                  console.log(`✅ ActivityForm: Step 4.4.${index + 1} - Successfully created relation: Activity ${numericActivityId} -> ${entity.type} ${entity.id}`, result);
                  return { success: true, entity };
                } catch (err) {
                  console.error(`❌ ActivityForm: Step 4.4.${index + 1} - Failed to create relation for ${entity.type} ${entity.id}:`, err);
                  return { success: false, error: err, entity };
                }
              });

            // Remove relationships that are no longer selected
            const relationsToDelete = (existingRelations.relations || [])
              .filter(rel => {
                // Handle different casing
                const srcType = rel.SourceEntityType || rel.sourceEntityType || '';
                const tgtType = rel.TargetEntityType || rel.targetEntityType || '';
                const srcId = rel.SourceEntityID || rel.sourceEntityID;
                const tgtId = rel.TargetEntityID || rel.targetEntityID;
                
                const isActivitySource = srcType.toLowerCase() === 'activity';
                const entityType = isActivitySource ? tgtType : srcType;
                const eId = isActivitySource ? tgtId : srcId;
                const entityKey = `${entityType}:${eId}`;
                return !selectedEntityKeys.has(entityKey);
              });
            
            console.log('🔗 ActivityForm: Step 4.5 - Deleting relationships:', {
              relationsToDelete,
              count: relationsToDelete.length
            });
            
            const deletePromises = relationsToDelete.map(async (rel, index) => {
                try {
                  const relationId = rel.RelationID || rel.relationID;
                  console.log(`🔗 ActivityForm: Step 4.5.${index + 1} - Deleting relation ${relationId}...`);
                  await withTimeout(
                    entityRelationService.delete(relationId),
                    6000,
                    `deleteRelation(${relationId})`
                  );
                  console.log(`✅ ActivityForm: Step 4.5.${index + 1} - Successfully deleted relation: ${relationId}`);
                  return { success: true, relationId: rel.RelationID };
                } catch (err) {
                  console.error(`❌ ActivityForm: Step 4.5.${index + 1} - Failed to delete relation ${rel.RelationID}:`, err);
                  return { success: false, error: err, relationId: rel.RelationID };
                }
              });

            // If no ops, skip quickly
            if (createPromises.length === 0 && deletePromises.length === 0) {
              console.log('⚡ ActivityForm: Step 4.6 - No relationship operations to perform, skipping');
            } else {
              console.log('🔗 ActivityForm: Step 4.6 - Executing all relationship operations in parallel...');
            
              // Execute all operations in parallel
              const [createResults, deleteResults] = await Promise.all([
                Promise.allSettled(createPromises),
                Promise.allSettled(deletePromises)
              ]);

              console.log('🔗 ActivityForm: Step 4.7 - Relationship operations completed:', {
                createResults,
                deleteResults,
                createResultsLength: createResults.length,
                deleteResultsLength: deleteResults.length
              });

              const created = createResults.filter(r => r.status === 'fulfilled' && r.value.success).length;
              const deleted = deleteResults.filter(r => r.status === 'fulfilled' && r.value.success).length;
              const failed = createResults.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)).length +
                            deleteResults.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)).length;

              console.log('🔗 ActivityForm: Step 4.8 - Relationship sync summary:', {
                created,
                deleted,
                failed,
                createResultsDetails: createResults.map(r => ({
                  status: r.status,
                  success: r.status === 'fulfilled' ? r.value.success : false,
                  error: r.status === 'rejected' ? r.reason : (r.status === 'fulfilled' && !r.value.success ? r.value.error : null)
                })),
                deleteResultsDetails: deleteResults.map(r => ({
                  status: r.status,
                  success: r.status === 'fulfilled' ? r.value.success : false,
                  error: r.status === 'rejected' ? r.reason : (r.status === 'fulfilled' && !r.value.success ? r.value.error : null)
                }))
              });

              if (failed > 0) {
                console.warn(`⚠️ ActivityForm: ${failed} relationship operation(s) failed`);
                setError(`Activity saved but ${failed} relationship operation(s) failed. Please check the console for details.`);
                toast.error(t('activities.relationshipsPartialFailure', 'Some relationships failed to sync'));
              } else {
                console.log(`✅ ActivityForm: All relationships synced successfully - ${created} created, ${deleted} deleted`);
                if (created + deleted > 0) {
                  toast.success(t('activities.relationshipsSynced', 'Relationships synced'));
                }
              }
            }
          } catch (relationError) {
            console.error('❌ ActivityForm: Relationship sync error:', relationError);
            console.error('❌ ActivityForm: Error details:', {
              message: relationError.message,
              stack: relationError.stack,
              response: relationError.response,
              data: relationError.data
            });

            // Fallback: if fetching existing relations timed out, attempt direct creates for all selected entities
            if ((relationError.message || '').includes('Timeout') && (formData.relatedEntities?.length || 0) > 0) {
              try {
                console.warn('⚠️ ActivityForm: Fallback - creating all relations without dedupe due to timeout...');
                const createAllResults = await Promise.allSettled(
                  (formData.relatedEntities || []).map(async (entity, index) => {
                    console.log(`🔗 Fallback create ${index + 1}: Activity ${numericActivityId} -> ${entity.type} ${entity.id}`);
                    const result = await entityRelationService.create({
                      sourceEntityType: 'Activity',
                      sourceEntityID: numericActivityId,
                      targetEntityType: entity.type,
                      targetEntityID: entity.id,
                      relationType: 'related_to'
                    });
                    return { success: true, entity, result };
                  })
                );
                const createdOk = createAllResults.filter(r => r.status === 'fulfilled').length;
                const createdFail = createAllResults.length - createdOk;
                console.log('✅ ActivityForm: Fallback create results:', { createdOk, createdFail, createAllResults });
                if (createdFail > 0) setError(`Some relationships failed to create (${createdFail}). See console.`);
                if (createdFail > 0) {
                  toast.error(t('activities.relationshipsCreatePartialFailure', 'Some relationships failed to create'));
                } else {
                  toast.success(t('activities.relationshipsCreated', 'Relationships created'));
                }
              } catch (fallbackErr) {
                console.error('❌ ActivityForm: Fallback relation creation failed:', fallbackErr);
                setError(`Activity saved but relationships could not be created: ${fallbackErr.message || 'Unknown error'}`);
                toast.error(t('activities.relationshipsCreateFailed', 'Failed to create relationships'));
              }
            } else {
              setError(`Activity saved but relationship sync failed: ${relationError.message || 'Unknown error'}`);
              toast.error(t('activities.relationshipsSyncFailed', 'Failed to sync relationships'));
            }
            // Don't block UI even if relationships fail
          }
          }
        } else {
          console.warn('⚠️ ActivityForm: No valid activityId for relationship sync:', {
            activityId,
            numericActivityId,
            activityIdType: typeof activityId,
            numericActivityIdType: typeof numericActivityId,
            isNaN: isNaN(numericActivityId),
            willSkip: true
          });
        }

        console.log('✅ ActivityForm: Step 5 - Save process completed, closing form...');
        
        // Finish UI flow - reset loading before closing to prevent stuck state
        setLoading(false);
        
        // Small delay to ensure state updates are processed
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Finish UI flow
        if (onSuccess) {
          console.log('✅ ActivityForm: Calling onSuccess callback...');
          onSuccess();
        }
        console.log('✅ ActivityForm: Calling onClose...');
        onClose();
      } else {
        console.error('❌ ActivityForm: Activity save failed with status:', response.status);
        let message = '';
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const errorData = await response.json();
            console.error('❌ ActivityForm: Error response (json):', errorData);
            message = errorData.error || errorData.message || errorData.details || '';
          } else {
            const text = await response.text();
            console.error('❌ ActivityForm: Error response (text):', text);
            message = text;
          }
        } catch (parseErr) {
          console.warn('⚠️ ActivityForm: Failed to parse error response');
        }
        // Friendly fallbacks by status
        if (!message) {
          if (response.status === 403) message = t('activities.permissionDenied', 'You do not have permission to edit this activity');
          else if (response.status === 401) message = t('activities.unauthorized', 'Your session has expired. Please log in again.');
          else if (response.status === 400) message = t('activities.validationFailed', 'Validation failed. Please check your input.');
          else message = t('activities.saveFailed', 'Failed to save activity');
        }
        setError(message);
        toast.error(message);
        setLoading(false);
      }
    } catch (err) {
      console.error('❌ ActivityForm: Submit failed with exception:', err);
      console.error('❌ ActivityForm: Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name,
        cause: err.cause
      });
      const msg = `An error occurred while saving the activity: ${err.message || 'Unknown error'}`;
      setError(msg);
      toast.error(msg);
      setLoading(false);
    }
  };

  // Monitor tenant changes and clear entity-related data only when tenant code actually changes
  useEffect(() => {
    const currentTenantCode = getProtectedTenantCode();
    const previousTenantCode = tenantCodeRef.current;
    
    if (previousTenantCode !== undefined && previousTenantCode !== currentTenantCode) {
      console.log('🔄 ActivityForm: Tenant changed, clearing entity-related selections');
      setFormData(prev => ({
        ...prev,
        selectedEntityTypes: [],
        relatedEntities: []
      }));
      
      setEntitySearchTerms({
        Customer: '',
        Lead: '',
        Opportunity: '',
        SaleNote: ''
      });
      
      setSearchResults({
        customers: [],
        leads: [],
        opportunities: [],
        activities: []
      });
      
      setShowEntitySearch({
        Customer: false,
        Lead: false,
        Opportunity: false,
        SaleNote: false
      });
    }
    // Update the ref to the latest tenant code
    tenantCodeRef.current = currentTenantCode;
  }, [getProtectedTenantCode]);

  // Show loading state while config is loading
  if (configLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-sm">
          <div className="flex flex-col justify-center items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Loading configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if config failed to load
  if (configError) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md">
          <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">Configuration Error</h3>
          <p className="text-red-600 dark:text-red-400 mb-4">{configError}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Unable to load entity configuration. Please try refreshing the page or contact support.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const handleEntityModalSearchChange = (value) => {
    setEntitySelectionModal(prev => ({ ...prev, searchTerm: value }));
    const entityType = entitySelectionModal.entityType;
    if (!entityType) return;
    if (searchTimeouts[entityType]) clearTimeout(searchTimeouts[entityType]);
    const timeoutId = setTimeout(() => performEntitySearchForModal(value, entityType), 300);
    setSearchTimeouts(prev => ({ ...prev, [entityType]: timeoutId }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-visible relative">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {activity ? t('activities.editActivity') : t('activities.newActivity')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <div className="max-h-[calc(90vh-120px)] overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Validation Summary */}
          <ValidationSummary 
            errors={fieldErrors} 
            onFieldClick={(field) => {
              const element = document.getElementById(field) || document.querySelector(`[name="${field}"]`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.focus?.();
              }
            }}
          />
          
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
              <span className="text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('activities.activityType')} <span className="text-red-500">*</span>
              </label>
              <select
                id="activityType"
                name="activityType"
                value={formData.activityType}
                onChange={(e) => handleChange('activityType', e.target.value)}
                className={getSelectClassName(!!fieldErrors.activityType)}
              >
                <option value="">{t('activities.selectActivityType', 'Select activity type')}</option>
                {(activityTypeOptions.length > 0 ? activityTypeOptions : activityTypes).map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              <FieldError error={fieldErrors.activityType} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('activities.priority')}
              </label>
              <select
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {priorityOptions.length > 0 ? (
                  priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="Low">{t('activities.priorities.low')}</option>
                    <option value="Medium">{t('activities.priorities.medium')}</option>
                    <option value="High">{t('activities.priorities.high')}</option>
                    <option value="Critical">{t('activities.priorities.critical')}</option>
                  </>
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('activities.status')}
              </label>
              <select
                value={formData.status}
                onChange={(e) => {
                  const newStatus = e.target.value;
                  handleChange('status', newStatus);
                  
                  // Auto-set dates based on status
                  const today = new Date().toISOString().split('T')[0];
                  
                  if (newStatus === 'In Progress' && !formData.startDate) {
                    handleChange('startDate', today);
                  } else if (newStatus === 'Done') {
                    if (!formData.startDate) {
                      handleChange('startDate', today);
                    }
                    if (!formData.endDate) {
                      handleChange('endDate', formData.startDate || today);
                    }
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {statusOptions.length > 0 ? (
                  statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="Planned">{t('activities.statuses.planned')}</option>
                    <option value="In Progress">{t('activities.statuses.inProgress')}</option>
                    <option value="Done">{t('activities.statuses.done')}</option>
                    <option value="On Hold">{t('activities.statuses.onHold')}</option>
                    <option value="Cancelled">{t('activities.statuses.cancelled')}</option>
                  </>
                )}
              </select>
            </div>
          </div>

          {/* Date Fields - Conditional based on status */}
          {formData.status === 'Planned' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('activities.dueDate')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="dueDate"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={(e) => handleChange('dueDate', e.target.value)}
                  className={getInputClassName(!!fieldErrors.dueDate)}
                />
                <FieldError error={fieldErrors.dueDate} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('activities.followUpDate')}
                </label>
                <input
                  type="date"
                  value={formData.followUpDate}
                  onChange={(e) => handleChange('followUpDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          {formData.status === 'In Progress' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('activities.startDate')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  className={getInputClassName(!!fieldErrors.startDate)}
                />
                <FieldError error={fieldErrors.startDate} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('activities.followUpDate')}
                </label>
                <input
                  type="date"
                  value={formData.followUpDate}
                  onChange={(e) => handleChange('followUpDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          {formData.status === 'Done' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('activities.startDate')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  className={getInputClassName(!!fieldErrors.startDate)}
                />
                <FieldError error={fieldErrors.startDate} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('activities.endDate')}
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          {/* Other statuses show all date fields */}
          {!['Planned', 'In Progress', 'Done'].includes(formData.status) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('activities.dueDate')}
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleChange('dueDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('activities.followUpDate')}
                </label>
                <input
                  type="date"
                  value={formData.followUpDate}
                  onChange={(e) => handleChange('followUpDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('activities.subject')} <span className="text-red-500">*</span>
            </label>
            {getSuggestionConfig(entityConfig, 'Subject') ? (
              <AutocompleteTextField
                entityName="activity"
                fieldName="Subject"
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                className={getInputClassName(!!fieldErrors.subject)}
                placeholder={t('activities.placeholders.subject')}
                suggestConfig={getSuggestionConfig(entityConfig, 'Subject')}
              />
            ) : (
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                className={getInputClassName(!!fieldErrors.subject)}
                placeholder={t('activities.placeholders.subject')}
              />
            )}
            <FieldError error={fieldErrors.subject} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('activities.description')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              placeholder={t('activities.placeholders.description')}
            />
          </div>

          {/* Related Entities (Enhanced) */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('activities.relatedEntities')}</h3>
              {formData.relatedEntities.length > 0 && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formData.relatedEntities.length} {formData.relatedEntities.length === 1 ? 'entity' : 'entities'} selected
                </span>
              )}
            </div>
            
            {/* Entity Type Cards - Click to Add */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {entityTypes.map(entityType => {
                const count = formData.relatedEntities.filter(e => e.type === entityType.value).length;
                const getIcon = () => {
                  switch (entityType.value) {
                    case 'Customer': return <Building className="h-5 w-5" />;
                    case 'Lead': return <User className="h-5 w-5" />;
                    case 'Opportunity': return <Target className="h-5 w-5" />;
                    case 'SaleNote': return <FileText className="h-5 w-5" />;
                    default: return <FileText className="h-5 w-5" />;
                  }
                };
                
                return (
                  <button
                    key={entityType.value}
                    type="button"
                    onClick={() => openEntitySelectionModal(entityType.value, '')}
                    className={`group relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 ${
                      count > 0
                        ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    <div className={`mb-2 transition-colors ${
                      count > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-blue-500'
                    }`}>
                      {getIcon()}
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white text-center">
                    {entityType.label}
                </div>
                    {count > 0 && (
                      <div className="absolute -top-2 -right-2 bg-blue-500 dark:bg-blue-600 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                        {count}
            </div>
                    )}
                    <Plus className={`absolute bottom-1 right-1 h-4 w-4 transition-opacity ${
                      count > 0 ? 'opacity-50' : 'opacity-0 group-hover:opacity-100'
                    } text-gray-400 dark:text-gray-500`} />
                  </button>
                );
              })}
                  </div>

            {/* Selected Entities Display - Chips Style */}
            {formData.relatedEntities.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Link className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Selected Entities ({formData.relatedEntities.length})
                  </span>
                </div>

                {/* Group by Entity Type */}
                {entityTypes.map(entityType => {
                  const entitiesOfType = formData.relatedEntities.filter(e => e.type === entityType.value);
                  if (entitiesOfType.length === 0) return null;
                  
                  const getIcon = () => {
                    switch (entityType.value) {
                      case 'Customer': return <Building className="h-4 w-4" />;
                      case 'Lead': return <User className="h-4 w-4" />;
                      case 'Opportunity': return <Target className="h-4 w-4" />;
                      case 'SaleNote': return <FileText className="h-4 w-4" />;
                      default: return <FileText className="h-4 w-4" />;
                    }
                  };
                  
                  return (
                    <div key={entityType.value} className="space-y-2">
                          <div className="flex items-center space-x-2">
                        {getIcon()}
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                          {entityType.label} ({entitiesOfType.length})
                              </span>
                          </div>
                      <div className="flex flex-wrap gap-2">
                        {entitiesOfType.map((entity, index) => (
                          <div
                            key={`${entity.type}-${entity.id}-${index}`}
                            className="group flex items-center space-x-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                          >
                            {getIcon()}
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {entity.name || `${entityType.value} ${entity.id}`}
                          </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              #{entity.id}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeEntity(entity.type, entity.id)}
                              className="ml-1 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                              title={`Remove ${entity.name}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                        </div>
                        ))}
                        {/* Add More Button */}
                      <button
                        type="button"
                          onClick={() => openEntitySelectionModal(entityType.value, '')}
                          className="flex items-center space-x-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 text-sm font-medium transition-colors"
                        >
                          <Plus className="h-3 w-3" />
                          <span>Add {entityType.label}</span>
                      </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Assignment */}
          <div>
            {/* Salesperson */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('activities.salesperson', 'Salesperson')}
              </label>
              <select
                value={formData.salespersonId}
                onChange={(e) => {
                  const newSalespersonId = e.target.value;
                  
                  // Set original salesperson ID if not set yet (on initial load)
                  if (!originalSalespersonID && newSalespersonId && activity) {
                    setOriginalSalespersonID(newSalespersonId);
                  }
                  
                  // Check for salesperson change warning
                  if (activity && originalSalespersonID) {
                    const hasChanged = newSalespersonId !== originalSalespersonID && newSalespersonId !== '';
                    const currentUserId = String(user?.tenantUserId || user?.userId || user?.UserID || '');
                    
                    // Show warning if policy is 1 or 2 and user is changing salesperson to someone else
                    if (hasChanged && dataGovernancePolicy) {
                      const policyType = Number(dataGovernancePolicy.policyType || dataGovernancePolicy.PolicyType || 0);
                      const isPolicy1Or2 = policyType === 1 || policyType === 2;
                      const isChangingToOther = newSalespersonId !== currentUserId;
                      
                      console.log('🔍 ActivityForm: Salesperson change check:', {
                        originalSalespersonID,
                        newSalespersonId,
                        currentUserId,
                        policyType,
                        isPolicy1Or2,
                        isChangingToOther,
                        dataGovernancePolicy
                      });
                      
                      if (isPolicy1Or2 && isChangingToOther) {
                        setShowSalespersonWarning(true);
                      } else {
                        setShowSalespersonWarning(false);
                      }
                    } else {
                      setShowSalespersonWarning(false);
                    }
                  } else {
                    setShowSalespersonWarning(false);
                  }
                  
                  handleChange('salespersonId', newSalespersonId);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('activities.selectSalesperson', 'Select Salesperson')}</option>
                {salespersons.map(salesperson => (
                  <option key={salesperson.UserID} value={salesperson.UserID}>
                    {salesperson.FirstName} {salesperson.LastName}
                  </option>
                ))}
              </select>
              {showSalespersonWarning && (
                <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start space-x-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                      {t('activities.salespersonChangeWarning', 'Warning: Changing the salesperson')}
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                      {t('activities.salespersonChangeWarningDetail', 'Under the current data governance policy, you may no longer be able to view or update this record after saving.')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Parent Activity */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('activities.parentActivity')}
            </label>
            <div className="relative" style={{ position: 'relative' }}>
              <input
                type="text"
                value={parentSearchTerm}
                onChange={(e) => {
                  setParentSearchTerm(e.target.value);
                  searchParentActivities(e.target.value);
                  setShowParentSearch(true);
                }}
                onFocus={() => setShowParentSearch(true)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                placeholder={t('activities.placeholders.searchParentActivity')}
              />
              <ArrowRight className="h-4 w-4 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            </div>

            {/* Parent Activity Search Results */}
            {showParentSearch && (
              <div className="search-dropdown absolute w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto" style={{ top: '100%', left: 0, right: 0, zIndex: 9999 }}>
                {searchLoading.activities ? (
                  <div className="px-3 py-2 text-gray-500 dark:text-gray-400 flex items-center space-x-2">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>Searching...</span>
                  </div>
                ) : (
                  <>
                    {searchResults.activities?.map((parentActivity, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleParentSelect(parentActivity)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                      >
                        <div className="flex items-center space-x-2">
                          <FileText className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <span className="text-gray-900 dark:text-white">{parentActivity.Subject}</span>
                          <span className="text-gray-500 dark:text-gray-400 text-sm">({parentActivity.ActivityType})</span>
                        </div>
                      </button>
                    ))}
                    {!searchLoading.activities && searchResults.activities?.length === 0 && parentSearchTerm.length >= 2 && (
                      <div className="px-3 py-2 text-gray-500 dark:text-gray-400">{t('activities.noResultsFound')}</div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>



          {/* Duration and Location */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('activities.estimatedDuration')}
              </label>
              <input
                type="number"
                value={formData.estimatedDuration}
                onChange={(e) => handleChange('estimatedDuration', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('activities.actualDuration')}
              </label>
              <input
                type="number"
                value={formData.actualDuration}
                onChange={(e) => handleChange('actualDuration', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('activities.location')}
              </label>
              {getSuggestionConfig(entityConfig, 'Location') ? (
                <AutocompleteTextField
                  entityName="activity"
                  fieldName="Location"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="Meeting location"
                  suggestConfig={getSuggestionConfig(entityConfig, 'Location')}
                />
              ) : (
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="Meeting location"
                />
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('activities.notes')}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              placeholder="Activity notes and details..."
            />
          </div>

          {/* Outcome (for completed activities) */}
          {formData.status === 'Done' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('activities.outcome')}
              </label>
              <textarea
                value={formData.outcome}
                onChange={(e) => handleChange('outcome', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                placeholder="Activity outcome and results..."
              />
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 dark:bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
                             {loading ? (
                 <>
                   <RefreshCw className="h-4 w-4 animate-spin" />
                   <span>{t('common.saving')}</span>
                 </>
               ) : (
                 <>
                   <Save className="h-4 w-4" />
                   <span>{activity ? t('common.save') : t('activities.createActivity')}</span>
                 </>
               )}
            </button>
          </div>
        </form>
        </div>
      </div>

      {/* Entity Selection Modal */}
      <EntitySelectionModal
        isOpen={entitySelectionModal.isOpen}
        entityType={entitySelectionModal.entityType}
        title={`Select ${entitySelectionModal.entityType || 'Entity'}`}
        subtitle={entitySelectionModal.selectedIds.length > 0 ? `${entitySelectionModal.selectedIds.length} ${t('activities.selected', 'selected')}` : undefined}
        searchTerm={entitySelectionModal.searchTerm}
        onSearchTermChange={handleEntityModalSearchChange}
        searchPlaceholder={t('activities.searchEntity', { entityType: entitySelectionModal.entityType })}
        loading={entitySelectionModal.loading}
        results={entitySelectionModal.results}
        selectedIds={entitySelectionModal.selectedIds}
        onToggleEntity={toggleEntitySelectionInModal}
        onClose={() => closeEntitySelectionModal(false)}
        onConfirm={() => closeEntitySelectionModal(true)}
        confirmLabel={
          entitySelectionModal.selectedIds.length > 0
            ? `${t('common.add', 'Add')} (${entitySelectionModal.selectedIds.length})`
            : t('common.add', 'Add')
        }
        confirmIcon={Plus}
        confirmDisabled={entitySelectionModal.selectedIds.length === 0}
        cancelLabel={t('common.cancel', 'Cancel')}
        emptyMessage={t('activities.noResultsFound', 'No results found')}
        idleMessage={t('activities.startTypingToSearch', 'Start typing to search')}
        loadingMessage={t('activities.searching', 'Searching...')}
        resolveId={(entity, type) => getEntityId(entity, type || entitySelectionModal.entityType)}
        resolveName={(entity, type) => getEntityName(entity, type || entitySelectionModal.entityType)}
        resolveSubInfo={(entity, type) => getEntitySubInfo(entity, type || entitySelectionModal.entityType)}
      />

    </div>
  );
};

export default ActivityForm; 